import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { V2Layout, NeonDivider, HeroDivider } from './V2Shared';
import SEO, { buildBreadcrumb, SITE_URL } from '../components/SEO';

/* ============== Shared editorial hero for legal pages ============== */
function LegalHero({ tag, title, subtitle, image, testId }) {
  const bgRef = useRef(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.2}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="v2-about-hero" data-testid={testId}>
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="v2-hero-grain" />
      <div className="v2-about-hero-body">
        <div className="v2-mono-tag">{tag}</div>
        <h1 className="v2-about-title">{title}</h1>
        <p className="v2-about-sub">{subtitle}</p>
      </div>
    </section>
  );
}

/* ============== Section primitive — editorial two-column row ============== */
function LegalSection({ num, tag, heading, children, testId }) {
  return (
    <section className="v2-section v2-section-dark v2-section-tight" data-testid={testId}>
      <div className="v2-container">
        <div className="v2-about-row">
          <div className="v2-about-row-meta">
            <div className="v2-mono-tag" style={{ marginBottom: 8 }}>{num} · {tag}</div>
          </div>
          <div className="v2-about-row-body" style={{ color: 'rgba(255,255,255,0.85)' }}>
            <h2 className="v2-about-h2">{heading}</h2>
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

const P = ({ children }) => (
  <p style={{ fontSize: 15, lineHeight: 1.7, marginBottom: 14, color: 'rgba(255,255,255,0.78)' }}>{children}</p>
);

const UL = ({ children }) => (
  <ul style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(255,255,255,0.78)', paddingLeft: 22, marginBottom: 14 }}>
    {children}
  </ul>
);

const EFFECTIVE_DATE = 'May 11, 2026';

/* ============== Privacy Policy ============== */
export function V2Privacy() {
  return (
    <V2Layout>
      <SEO
        title="Privacy Policy | WesTex Legal"
        description="How WesTex Legal collects, uses, and protects your information across our process serving, notary, and client portal services."
        path="/privacy"
        image={`${SITE_URL}/lubbock-about.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'Privacy Policy', path: '/privacy' },
        ])}
      />
      <LegalHero
        tag="LEGAL · PRIVACY"
        title={<>Your information,<br /><span className="v2-about-title-line2"><em>handled with care.</em></span></>}
        subtitle="How we collect, use, and protect information across our process serving, notary, and portal services."
        image="/lubbock-about.jpg"
        testId="v2-privacy-hero"
      />
      <HeroDivider />

      <section className="v2-section v2-section-dark v2-section-tight">
        <div className="v2-container">
          <div className="v2-about-row">
            <div className="v2-about-row-meta">
              <div className="v2-mono-tag">EFFECTIVE</div>
            </div>
            <div className="v2-about-row-body" style={{ color: 'rgba(255,255,255,0.85)' }}>
              <p className="v2-mono-sm" style={{ color: 'var(--pv2-text-muted, rgba(255,255,255,0.55))', fontFamily: 'JetBrains Mono, monospace' }}>
                Effective {EFFECTIVE_DATE}
              </p>
              <P>
                WesTex Legal LLC ("WesTex Legal," "we," "our," or "us") operates the website at westexlegal.com,
                a client portal, and provides process serving, notary, and skip-tracing services across Texas, New
                Mexico, and nationwide. This Privacy Policy explains what information we collect, how we use it, and
                the choices you have. By using our services you agree to the practices described below.
              </P>
            </div>
          </div>
        </div>
      </section>

      <NeonDivider delay={0.3} />

      <LegalSection num="01" tag="INFORMATION WE COLLECT" heading="What we collect — and why." testId="v2-privacy-collect">
        <P>We collect only the information needed to do our job well and meet legal record-keeping requirements.</P>
        <UL>
          <li><strong>Contact &amp; identity:</strong> name, email address, phone number, business or firm name.</li>
          <li><strong>Service details:</strong> recipient information, service addresses, case numbers, court information, and supporting documents you upload for service.</li>
          <li><strong>Payment information:</strong> processed by Stripe — we do not store full card numbers on our servers. We retain transaction metadata (amount, last 4 digits, receipt URL).</li>
          <li><strong>Account credentials:</strong> hashed passwords, login activity timestamps, IP addresses for security and abuse detection.</li>
          <li><strong>Notary appointment data:</strong> appointment time, meeting address, attendee names, witness count, ID-check confirmation.</li>
          <li><strong>Field documentation:</strong> service attempt notes, GPS coordinates, time-stamped photos uploaded by our process servers as legal proof of service.</li>
          <li><strong>Analytics:</strong> standard web logs (pages viewed, browser, referrer) used solely to operate the site.</li>
        </UL>
      </LegalSection>

      <NeonDivider delay={0.4} />

      <LegalSection num="02" tag="HOW WE USE IT" heading="What we do with information." testId="v2-privacy-use">
        <UL>
          <li>Carry out the process serving, notary, or skip-tracing service you requested.</li>
          <li>Generate court-ready Affidavits of Service and required legal records.</li>
          <li>Communicate updates, invoices, payment confirmations, and required status notices.</li>
          <li>Process payments through Stripe and reconcile invoices.</li>
          <li>Comply with Texas Rules of Civil Procedure, JBCC certification rules, and equivalent rules in jurisdictions where we serve.</li>
          <li>Detect fraud, secure portal accounts, and protect against unauthorized access.</li>
        </UL>
        <P>We do not sell or rent your personal information. We do not use your data to train artificial intelligence models.</P>
      </LegalSection>

      <NeonDivider delay={0.5} />

      <LegalSection num="03" tag="SERVICE PROVIDERS" heading="Who we share data with." testId="v2-privacy-share">
        <P>We share information only with vetted service providers that help us deliver our services:</P>
        <UL>
          <li><strong>Stripe</strong> — payment processing (PCI-DSS Level 1).</li>
          <li><strong>ServeManager</strong> — process-serving case management and field attempt tracking.</li>
          <li><strong>Google</strong> — Calendar integration for notary appointment scheduling (the WesTex Legal admin Google account only). We use the <em>calendar.events</em> scope strictly to create, view, and update notary appointment events. Google data is never sold, shared, or used for advertising.</li>
          <li><strong>Email infrastructure</strong> — transactional email delivery for invoices, confirmations, and status notifications.</li>
          <li><strong>Courts &amp; opposing parties</strong> — when service of process is completed, the resulting Affidavit of Service is filed with the appropriate court as required by law.</li>
        </UL>
        <P>We may also disclose information when required by subpoena, court order, or applicable law.</P>
      </LegalSection>

      <NeonDivider delay={0.6} />

      <LegalSection num="04" tag="DATA RETENTION" heading="How long we keep it." testId="v2-privacy-retention">
        <P>
          We retain records consistent with state-bar and JBCC rules for process-serving documentation —
          generally a minimum of seven (7) years from the date of service. Notary records are retained for the
          period required by Texas Government Code Chapter 406. You may request earlier deletion of non-mandatory
          records at any time (see Section 06).
        </P>
      </LegalSection>

      <NeonDivider delay={0.7} />

      <LegalSection num="05" tag="SECURITY" heading="How we protect it." testId="v2-privacy-security">
        <UL>
          <li>All traffic is encrypted in transit via TLS 1.2+.</li>
          <li>Passwords are hashed using industry-standard bcrypt.</li>
          <li>Portal access requires authentication; admin access requires elevated credentials.</li>
          <li>Payment card data is tokenized by Stripe and never touches our servers.</li>
          <li>Backups and database access are restricted to authorized personnel only.</li>
        </UL>
        <P>No system is perfectly secure. If we ever detect a breach affecting your information, we will notify you in accordance with applicable Texas notification law.</P>
      </LegalSection>

      <NeonDivider delay={0.8} />

      <LegalSection num="06" tag="YOUR RIGHTS" heading="The choices you have." testId="v2-privacy-rights">
        <P>You may, at any time:</P>
        <UL>
          <li>Request a copy of the personal information we hold about you.</li>
          <li>Ask us to correct inaccurate information.</li>
          <li>Request deletion of any non-mandatory information (subject to legal retention requirements above).</li>
          <li>Opt out of non-essential email communications via the unsubscribe link in any message.</li>
          <li>Close your client portal account.</li>
        </UL>
        <P>
          To exercise any of these rights, email <a href="mailto:WesTexLegal@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>WesTexLegal@gmail.com</a>{' '}
          or call (806) 401-1749. We respond within 30 days.
        </P>
      </LegalSection>

      <NeonDivider delay={0.9} />

      <LegalSection num="07" tag="COOKIES" heading="Cookies &amp; tracking." testId="v2-privacy-cookies">
        <P>
          We use only essential cookies necessary to operate the client portal (authentication session,
          security tokens). We do not use third-party advertising or behavioral tracking cookies. We do not
          participate in cross-site advertising networks.
        </P>
      </LegalSection>

      <NeonDivider delay={1.0} />

      <LegalSection num="08" tag="CHILDREN" heading="Children's information." testId="v2-privacy-children">
        <P>
          Our services are intended for legal professionals and clients age 18+. We do not knowingly collect
          information from anyone under 18. If you believe we have inadvertently received such information,
          contact us and we will delete it.
        </P>
      </LegalSection>

      <NeonDivider delay={1.1} />

      <LegalSection num="09" tag="CHANGES" heading="Updates to this policy." testId="v2-privacy-changes">
        <P>
          We may revise this Privacy Policy as our services evolve. When we make material changes, we will
          update the effective date above and, where appropriate, notify portal users by email.
        </P>
      </LegalSection>

      <NeonDivider delay={1.2} />

      <LegalSection num="10" tag="CONTACT" heading="How to reach us." testId="v2-privacy-contact">
        <P>
          WesTex Legal LLC<br />
          Lubbock, Texas<br />
          <a href="mailto:WesTexLegal@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>WesTexLegal@gmail.com</a><br />
          (806) 401-1749
        </P>
        <P style={{ marginTop: 24, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
          See also: <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link>.
        </P>
      </LegalSection>
    </V2Layout>
  );
}

/* ============== Terms of Service ============== */
export function V2Terms() {
  return (
    <V2Layout>
      <SEO
        title="Terms of Service | WesTex Legal"
        description="The terms governing your use of WesTex Legal's process serving, notary, and client portal services."
        path="/terms"
        image={`${SITE_URL}/lubbock-services.jpg`}
        schema={buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: 'Terms of Service', path: '/terms' },
        ])}
      />
      <LegalHero
        tag="LEGAL · TERMS"
        title={<>Terms of <em>Service.</em></>}
        subtitle="The agreement between you and WesTex Legal LLC when you request process serving, notary, or related professional services."
        image="/lubbock-services.jpg"
        testId="v2-terms-hero"
      />
      <HeroDivider />

      <section className="v2-section v2-section-dark v2-section-tight">
        <div className="v2-container">
          <div className="v2-about-row">
            <div className="v2-about-row-meta">
              <div className="v2-mono-tag">EFFECTIVE</div>
            </div>
            <div className="v2-about-row-body" style={{ color: 'rgba(255,255,255,0.85)' }}>
              <p className="v2-mono-sm" style={{ color: 'var(--pv2-text-muted, rgba(255,255,255,0.55))', fontFamily: 'JetBrains Mono, monospace' }}>
                Effective {EFFECTIVE_DATE}
              </p>
              <P>
                These Terms of Service ("Terms") govern your access to and use of services provided by WesTex Legal LLC
                ("WesTex Legal," "we," "our"), including process serving, notarization, skip tracing, and our online
                client portal at westexlegal.com. By submitting a job request, booking a notary appointment, or
                creating a portal account, you agree to these Terms.
              </P>
            </div>
          </div>
        </div>
      </section>

      <NeonDivider delay={0.3} />

      <LegalSection num="01" tag="SERVICES" heading="What we provide." testId="v2-terms-services">
        <UL>
          <li><strong>Process Serving</strong> — service of subpoenas, summons, citations, and other legal documents in accordance with Texas Rules of Civil Procedure and applicable jurisdiction rules.</li>
          <li><strong>Notary Services</strong> — including mobile notary, jail notary, hospital notary, weekend/holiday notary, and online (remote) notarization where permitted by law.</li>
          <li><strong>Skip Tracing</strong> — locating individuals for legal service purposes only.</li>
          <li><strong>Affidavits &amp; Court Filings</strong> — providing court-ready proofs of service.</li>
        </UL>
        <P>
          WesTex Legal is JBCC-certified and adheres to all applicable Texas professional rules.
          <strong> We do not provide legal advice.</strong> Nothing on this website or in any communication
          should be construed as legal advice or the formation of an attorney-client relationship.
        </P>
      </LegalSection>

      <NeonDivider delay={0.4} />

      <LegalSection num="02" tag="FEES &amp; PAYMENT" heading="Pricing and billing." testId="v2-terms-fees">
        <P>
          Fees are published on our website and may be updated from time to time. Notary fees are presented at
          the time of booking; process serving fees are zone-based and confirmed at the time of job acceptance.
          Mileage surcharges apply to notary travel beyond the published free radius and are calculated using
          the current federal mileage reimbursement rate.
        </P>
        <UL>
          <li>Payment is due upon booking for notary services.</li>
          <li>Process serving invoices are due within the terms shown on the invoice (typically Net 7 or Net 15).</li>
          <li>Past-due invoices are subject to late fees and may pause new service for that client until resolved.</li>
          <li>All payments are processed securely through Stripe.</li>
          <li>Refunds: notary appointments canceled at least 24 hours in advance are eligible for full refund. No-shows and same-day cancellations are non-refundable. Process serving fees are non-refundable once a service attempt has been made.</li>
        </UL>
      </LegalSection>

      <NeonDivider delay={0.5} />

      <LegalSection num="03" tag="CLIENT OBLIGATIONS" heading="What we need from you." testId="v2-terms-obligations">
        <UL>
          <li>Provide accurate recipient information, service addresses, and case details.</li>
          <li>Upload complete, legible copies of documents to be served.</li>
          <li>Disclose any known safety concerns or special circumstances regarding the recipient.</li>
          <li>Ensure all individuals signing notarized documents bring valid government-issued photo ID.</li>
          <li>Cooperate with our process servers and follow reasonable requests for clarifying information.</li>
        </UL>
        <P>
          You represent that you have lawful authority to request the service you are ordering and that the
          documents involved are authentic, properly issued, and lawfully obtained.
        </P>
      </LegalSection>

      <NeonDivider delay={0.6} />

      <LegalSection num="04" tag="PROHIBITED USES" heading="What you may not do." testId="v2-terms-prohibited">
        <P>You agree not to use our services to:</P>
        <UL>
          <li>Harass, stalk, or intimidate any person.</li>
          <li>Serve documents that are forged, altered, or not legitimately issued.</li>
          <li>Attempt to obtain personal information for purposes other than lawful service of process.</li>
          <li>Access another user's portal account without authorization.</li>
          <li>Reverse engineer, scrape, or attempt to disrupt our website or portal.</li>
        </UL>
        <P>We reserve the right to refuse or terminate service if we reasonably believe these Terms are being violated.</P>
      </LegalSection>

      <NeonDivider delay={0.7} />

      <LegalSection num="05" tag="LIABILITY" heading="Limitation of liability." testId="v2-terms-liability">
        <P>
          We provide our services with professional care and in accordance with applicable law. However, to the
          maximum extent permitted by law:
        </P>
        <UL>
          <li>Our total aggregate liability for any claim arising from or relating to the services is limited to the fees you actually paid us for the specific service giving rise to the claim.</li>
          <li>We are not liable for delays caused by recipient evasion, incorrect addresses, weather, court closures, or other circumstances beyond our reasonable control.</li>
          <li>We are not liable for indirect, incidental, special, consequential, or punitive damages.</li>
          <li>We make no warranty that a particular recipient will be located or successfully served — we warrant only that we will exercise diligent effort consistent with Texas professional standards.</li>
        </UL>
      </LegalSection>

      <NeonDivider delay={0.8} />

      <LegalSection num="06" tag="INDEMNIFICATION" heading="Your responsibilities to us." testId="v2-terms-indemnification">
        <P>
          You agree to defend and indemnify WesTex Legal against any claim arising from (a) information you
          provided that turns out to be false or misleading, (b) documents you supplied that were not lawfully
          authorized to be served, or (c) your violation of these Terms.
        </P>
      </LegalSection>

      <NeonDivider delay={0.9} />

      <LegalSection num="07" tag="INTELLECTUAL PROPERTY" heading="What belongs to whom." testId="v2-terms-ip">
        <P>
          The WesTex Legal name, logo, website design, portal software, and all related content are our property.
          You may not copy, modify, or distribute them without permission. Documents you upload remain your
          property; you grant us a limited license to use them solely to perform the services you requested.
        </P>
      </LegalSection>

      <NeonDivider delay={1.0} />

      <LegalSection num="08" tag="ACCOUNT TERMINATION" heading="Ending the relationship." testId="v2-terms-termination">
        <P>
          You may close your portal account at any time by contacting us. We may suspend or close accounts that
          violate these Terms or that remain inactive for extended periods. Outstanding invoices remain due
          after termination.
        </P>
      </LegalSection>

      <NeonDivider delay={1.1} />

      <LegalSection num="09" tag="GOVERNING LAW" heading="Law and venue." testId="v2-terms-law">
        <P>
          These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles.
          Any dispute arising from these Terms or our services shall be resolved exclusively in the state or
          federal courts located in Lubbock County, Texas, and you consent to personal jurisdiction in those courts.
        </P>
      </LegalSection>

      <NeonDivider delay={1.2} />

      <LegalSection num="10" tag="CHANGES" heading="Updates to these terms." testId="v2-terms-changes">
        <P>
          We may update these Terms periodically. Material changes will be communicated to active portal users by
          email and reflected by an updated effective date above. Continued use of the services after changes
          take effect constitutes acceptance of the revised Terms.
        </P>
      </LegalSection>

      <NeonDivider delay={1.3} />

      <LegalSection num="11" tag="CONTACT" heading="Questions or concerns." testId="v2-terms-contact">
        <P>
          WesTex Legal LLC<br />
          Lubbock, Texas<br />
          <a href="mailto:WesTexLegal@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>WesTexLegal@gmail.com</a><br />
          (806) 401-1749
        </P>
        <P style={{ marginTop: 24, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
          See also: <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>.
        </P>
      </LegalSection>
    </V2Layout>
  );
}
